import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useParams } from 'react-router-dom';
import { t } from '../../../../utils/textUtils';
import { useModularUnits } from '../hooks/useModularUnits';
import StructureUnit from './StructureUnit';
import UnitDialog from './UnitDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import CategoryDialog from './CategoryDialog';
import AddTaskDialog from './AddTaskDialog';

export default function StructureCard() {
  const { projectId } = useParams();
  const { units, loading, error, refresh, createUnit, updateUnit, deleteUnit, checkUnitCanBeDeleted, createCategory, updateCategory, checkCategoryCanBeDeleted, deleteCategory, createTask } = useModularUnits();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryDialogMode, setCategoryDialogMode] = useState('add'); // 'add' or 'edit'
  const [selectedUnitForCategory, setSelectedUnitForCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedCategoryForTask, setSelectedCategoryForTask] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleEditUnit = (unit) => {
    setSelectedUnit(unit);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handleAddUnit = () => {
    setSelectedUnit(null);
    setDialogMode('add');
    setDialogOpen(true);
  };

  const handleSaveUnit = async (unitData) => {
    try {
      setSaving(true);
      
      if (dialogMode === 'edit' && selectedUnit) {
        // Edit mode
        const unitId = selectedUnit.id ?? selectedUnit._id ?? selectedUnit.modularUnitId;
        await updateUnit(unitId, unitData);
        setSnackbar({
          open: true,
          message: 'Unit bangunan berhasil diperbarui',
          severity: 'success'
        });
      } else {
        // Add mode
        await createUnit(unitData);
        setSnackbar({
          open: true,
          message: 'Unit bangunan berhasil ditambahkan',
          severity: 'success'
        });
      }
      
      setDialogOpen(false);
      setSelectedUnit(null);
    } catch (error) {
      console.error('Error saving unit:', error);
      const errorMessage = dialogMode === 'edit' 
        ? 'Gagal memperbarui unit bangunan. Silakan coba lagi.'
        : 'Gagal menambahkan unit bangunan. Silakan coba lagi.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUnit = (unit) => {
    try {
      const unitId = unit.id ?? unit._id ?? unit.modularUnitId;
      
      // Check if unit can be deleted
      const checkResult = checkUnitCanBeDeleted(unitId);
      
      if (!checkResult.canDelete) {
        setSnackbar({
          open: true,
          message: 'Bangunan tidak dapat dihapus karena mempunyai kategori dan daftar pekerjaan didalamnya. Silahkan kosongkan terlebih dahulu untuk dapat menghapus bangunan.',
          severity: 'error'
        });
        return;
      }
      
      // If can be deleted, show confirmation dialog
      setUnitToDelete(unit);
      setDeleteDialogOpen(true);
    } catch (error) {
      console.error('Error checking unit for deletion:', error);
      setSnackbar({
        open: true,
        message: 'Gagal memeriksa bangunan. Silakan coba lagi.',
        severity: 'error'
      });
    }
  };

  const handleConfirmDelete = async () => {
    if (!unitToDelete) return;
    
    try {
      setSaving(true);
      const unitId = unitToDelete.id ?? unitToDelete._id ?? unitToDelete.modularUnitId;
      await deleteUnit(unitId);
      
      setDeleteDialogOpen(false);
      setUnitToDelete(null);
      setSnackbar({
        open: true,
        message: 'Bangunan berhasil dihapus',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting unit:', error);
      setSnackbar({
        open: true,
        message: 'Gagal menghapus bangunan. Silakan coba lagi.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddCategory = (unitId) => {
    // Find the unit object by ID
    const unit = units.find(u => {
      const id = u?.id ?? u?._id ?? u?.modularUnitId;
      return id === unitId;
    });
    
    if (unit) {
      setSelectedUnitForCategory(unit);
      setSelectedCategory(null);
      setCategoryDialogMode('add');
      setCategoryDialogOpen(true);
    }
  };

  const handleEditCategory = (category) => {
    setSelectedCategory(category);
    setSelectedUnitForCategory(null);
    setCategoryDialogMode('edit');
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      setSaving(true);
      
      if (categoryDialogMode === 'edit' && selectedCategory) {
        // Edit mode
        const categoryId = selectedCategory.id ?? selectedCategory._id ?? selectedCategory.categoryId;
        await updateCategory(categoryId, categoryData);
        setSnackbar({
          open: true,
          message: 'Kategori pekerjaan berhasil diperbarui',
          severity: 'success'
        });
      } else if (categoryDialogMode === 'add' && selectedUnitForCategory) {
        // Add mode
        const unitId = selectedUnitForCategory.id ?? selectedUnitForCategory._id ?? selectedUnitForCategory.modularUnitId;
        await createCategory(unitId, categoryData);
        setSnackbar({
          open: true,
          message: 'Kategori pekerjaan berhasil ditambahkan',
          severity: 'success'
        });
      }
      
      setCategoryDialogOpen(false);
      setSelectedUnitForCategory(null);
      setSelectedCategory(null);
    } catch (error) {
      console.error('Error saving category:', error);
      const errorMessage = categoryDialogMode === 'edit' 
        ? 'Gagal memperbarui kategori pekerjaan. Silakan coba lagi.'
        : 'Gagal menambahkan kategori pekerjaan. Silakan coba lagi.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = (category) => {
    try {
      const categoryId = category.id ?? category._id ?? category.categoryId;
      
      // Check if category can be deleted
      const checkResult = checkCategoryCanBeDeleted(categoryId);
      
      if (!checkResult.canDelete) {
        setSnackbar({
          open: true,
          message: 'Kategori tidak dapat dihapus karena mempunyai daftar pekerjaan didalamnya. Silahkan kosongkan terlebih dahulu untuk dapat menghapus kategori.',
          severity: 'error'
        });
        return;
      }
      
      // If can be deleted, show confirmation dialog
      setCategoryToDelete(category);
      setDeleteCategoryDialogOpen(true);
    } catch (error) {
      console.error('Error checking category for deletion:', error);
      setSnackbar({
        open: true,
        message: 'Gagal memeriksa kategori. Silakan coba lagi.',
        severity: 'error'
      });
    }
  };

  const handleConfirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    try {
      setSaving(true);
      const categoryId = categoryToDelete.id ?? categoryToDelete._id ?? categoryToDelete.categoryId;
      await deleteCategory(categoryId);
      
      setDeleteCategoryDialogOpen(false);
      setCategoryToDelete(null);
      setSnackbar({
        open: true,
        message: 'Kategori berhasil dihapus',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting category:', error);
      setSnackbar({
        open: true,
        message: 'Gagal menghapus kategori. Silakan coba lagi.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddTask = (category) => {
    setSelectedCategoryForTask(category);
    setTaskDialogOpen(true);
  };

  const handleSaveTask = async (taskData) => {
    if (!selectedCategoryForTask) return;
    
    try {
      setSaving(true);
      const categoryId = selectedCategoryForTask.id ?? selectedCategoryForTask._id ?? selectedCategoryForTask.categoryId;
      await createTask(categoryId, taskData);
      
      setTaskDialogOpen(false);
      setSelectedCategoryForTask(null);
      setSnackbar({
        open: true,
        message: 'Pekerjaan berhasil ditambahkan',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating task:', error);
      setSnackbar({
        open: true,
        message: 'Gagal menambahkan pekerjaan. Silakan coba lagi.',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, border: '1px solid #e5e7eb' }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('STRUKTUR PEKERJAAN')}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={refresh}>
            {t('SEGARKAN')}
          </Button>
          <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={handleAddUnit}>
            {t('TAMBAH UNIT BANGUNAN BARU')}
          </Button>
        </Stack>
      </Box>
      <Divider />

      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('GAGAL MEMUAT STRUKTUR PEKERJAAN')} — <Box component="span" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={refresh}>{t('COBA LAGI')}</Box>
          </Alert>
        ) : units.length === 0 ? (
          <Typography color="text.secondary">{t('BELUM ADA UNIT BANGUNAN.')}</Typography>
        ) : (
          <Box>
            {units.map((u, idx) => {
              const key = u?.id ?? u?._id ?? u?.modularUnitId ?? `unit-${idx}`;
              return (
                <StructureUnit key={key} unit={u} onEditUnit={handleEditUnit} onDeleteUnit={handleDeleteUnit} onAddCategory={handleAddCategory} onEditCategory={handleEditCategory} onDeleteCategory={handleDeleteCategory} onAddTask={handleAddTask} />
              );
            })}
          </Box>
        )}
      </Box>

      <UnitDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedUnit(null);
        }}
        onSave={handleSaveUnit}
        loading={saving}
        mode={dialogMode}
        unitData={selectedUnit}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUnitToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        loading={saving}
        unitName={unitToDelete?.name || ''}
      />

      <CategoryDialog
        open={categoryDialogOpen}
        onClose={() => {
          setCategoryDialogOpen(false);
          setSelectedUnitForCategory(null);
          setSelectedCategory(null);
        }}
        onSave={handleSaveCategory}
        loading={saving}
        mode={categoryDialogMode}
        categoryData={selectedCategory}
      />

      <DeleteConfirmDialog
        open={deleteCategoryDialogOpen}
        onClose={() => {
          setDeleteCategoryDialogOpen(false);
          setCategoryToDelete(null);
        }}
        onConfirm={handleConfirmDeleteCategory}
        loading={saving}
        unitName={categoryToDelete?.name || ''}
      />

      <AddTaskDialog
        open={taskDialogOpen}
        onClose={() => {
          setTaskDialogOpen(false);
          setSelectedCategoryForTask(null);
        }}
        onSave={handleSaveTask}
        loading={saving}
        categoryData={selectedCategoryForTask}
        projectId={projectId}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </Paper>
  );
}
